import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { UrlService } from '@shared/services/url/url.service';
import { IImeiStepService } from '@app/shared/tokens/imei-step.token';
import { TvfRequestsService } from '../../services/tvf-requests.service';
import { RelapseStateService } from './relapse.state';
import { SetImeiBiped } from '@app/store/checklist/checklist.state';

export interface IRelapseTradeResult {
  invoiceNumber: string;
  tradeId: string;
  imei: string;
  classification: string | null;
  model: string | null;
  brand?: string | null;
  productDescription?: string | null;
  recurrenceId?: string;
}

interface IRelapseTradeSearchResponse {
  data: IRelapseTradeResult;
  success: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RelapseService implements IImeiStepService {
  constructor(
    private _http: HttpClient,
    private _urlService: UrlService,
    private _tvfRequestsService: TvfRequestsService,
    private _stateService: RelapseStateService,
    private _store: Store
  ) {}

  searchTrades(query: string): Observable<IRelapseTradeResult[]> {
    const url = this._urlService.getUrl('COLLECT_TVF.searchRelapseTradesByFilter', {
      filter: query,
    });
    return this._http
      .get<IRelapseTradeSearchResponse>(url)
      .pipe(map((response) => [response.data]));
  }

  validateImei(imei: string, idTrade: string): Observable<any> {
    const activeIdTrade = idTrade || this._stateService.getSelectedTradeId();
    return this._tvfRequestsService.isImeiBipedAvailable(imei, activeIdTrade);
  }

  confirmImei(imei: string, idTrade: string): Observable<any> {
    const activeIdTrade = idTrade || this._stateService.getSelectedTradeId();
    return this._tvfRequestsService.sendImei(imei, activeIdTrade).pipe(
      tap(() => {
        this._store.dispatch(new SetImeiBiped(imei));
      })
    );
  }

  getRelapseQuestions(tradeId: string, answerId: string = ''): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.getRelapseQuestions', { tradeId, answerId });
    return this._http.get(url);
  }

  getHistoryTrade(imei: string): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.getHistoryTradeByImei', { imei });
    return this._http.get(url);
  }

  getRecurrences(search: string = '', page: number = 1, pageSize: number = 10): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.listRecurrences', {
      search,
      page,
      pageSize,
    });
    return this._http.get(url).pipe(catchError((error: any) => throwError(() => error)));
  }

  exportRecurrencesExcel(search: string, page: number, pageSize: number): Observable<Blob> {
    const url = this._urlService.getUrl('COLLECT_TVF.exportRecurrencesExcel', {
      search,
      page,
      pageSize,
    });
    return this._http.get(url, { responseType: 'blob' });
  }

  saveRecurrence(payload: any) {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.saveRecurrence');
    return this._http.post(url, payload).pipe(catchError((error: any) => throwError(() => error)));
  }

  updateRecurrence(payload: any) {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.updateRecurrence');
    return this._http.put(url, payload).pipe(catchError((error: any) => throwError(() => error)));
  }

  sendLaudoB2C(payload: any): Observable<Blob> {
    const url = this._urlService.getUrl('COLLECT_TVF.downloadLaudoPdfB2c');
    const headers = { 'accept': 'application/pdf', 'Content-Type': 'application/json' };
    return this._http.post(url, payload, { headers, responseType: 'blob' });
  }

  getLaudoB2B(tradesId: string[]): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.getLaudoDataB2b');
    return this._http.post(url, { tradesId }).pipe(catchError((error: any) => throwError(() => error)));
  }

  sendLaudoB2B(payload: any): Observable<Blob> {
    const url = this._urlService.getUrl('COLLECT_TVF.downloadLaudoPdfB2b');
    const headers = { 'accept': 'text/plain', 'Content-Type': 'application/json' };
    return this._http.post(url, payload, { headers, responseType: 'blob' });
  }

  getLaudoB2C(tradesId: string[]): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.getLaudoDataB2c');
    return this._http.post(url, { tradesId }).pipe(catchError((error: any) => throwError(() => error)));
  }
}
